import { useEffect, useRef } from "react";

import { useDispatch, useSelector, useStore } from "react-redux";

import type { CrmFiltersModel } from "app/pages/sales-intelligence/domain/crm-integration/types/custom-filter-values";
import { resolveActiveCrmIntegration } from "app/pages/sales-intelligence/domain/crm-integration/utils/active-crm-integration";
import useIntegrationCustomFiltersDescriptionQuery from "app/pages/sales-intelligence/modules/crm-integration/query/queries/useIntegrationCustomFiltersDescriptionQuery";
import {
    selectIsCrmNotIntegrated,
    selectIsCrmStatusLoading,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/crmSettingsSelectors";
import type { RootState } from "single-spa/store/types";

import { updateLeadsFilterValue } from "../store/action-creators";
import { selectIsCrmApplied } from "../store/selectors";
import { providerToApiCategory } from "../types/leadsSearchFilters.dto";

export const useCrmFiltersReset = (isEnabled: boolean): void => {
    const dispatch = useDispatch();

    const store = useStore<RootState>();

    const { data } = useIntegrationCustomFiltersDescriptionQuery(resolveActiveCrmIntegration);
    const provider = data?.integrationName;
    const apiCategory = provider ? providerToApiCategory[provider] : undefined;

    const isNotIntegrated = useSelector((state: RootState) => selectIsCrmNotIntegrated(state, provider));
    const isStatusLoading = useSelector((state: RootState) => selectIsCrmStatusLoading(state, provider));

    const lastSettledIsNotIntegratedRef = useRef<boolean | undefined>(undefined);

    useEffect(() => {
        if (!isEnabled || !provider || !apiCategory || isStatusLoading) {
            return;
        }

        const wasNotIntegrated = lastSettledIsNotIntegratedRef.current;
        lastSettledIsNotIntegratedRef.current = isNotIntegrated;

        const justDeintegrated = wasNotIntegrated === false && isNotIntegrated;
        if (!justDeintegrated) {
            return;
        }

        if (selectIsCrmApplied(store.getState(), apiCategory)) {
            // Clear the CRM unit by writing an empty request array; the resolver then omits the category.
            dispatch(
                updateLeadsFilterValue({
                    [apiCategory]: [] satisfies CrmFiltersModel,
                }),
            );
        }
    }, [isEnabled, provider, apiCategory, isNotIntegrated, isStatusLoading, store, dispatch]);
};
